/**
 * Elenco eventi (V25), la scheda Eventi di Modifica della partita conclusa:
 * correzione della partita da scrivania.
 *
 * Tutti gli eventi in ordine di minuto, in una tabella compatta come la
 * cronaca divisa per tempo, una riga ciascuno con chi li ha scritti e il
 * comando a icona; i cancellati si mostrano a richiesta con chi
 * li ha cancellati e si ripristinano. In testa le incoerenze che il ricalcolo
 * trova, con l'evento che le genera. Serve a partita conclusa, senza
 * sessione: durante la partita gli eventi si correggono dal timer. Ogni
 * correzione fa rileggere anche la testata della pagina, con il risultato.
 *
 * Gli eventi di periodo non hanno cestino ne ripristino: si governano dai
 * comandi del cronometro, e il database li difende comunque.
 */

import { Fragment, useState } from 'react';
import { useParams } from 'react-router-dom';
import type { Formato, Incoerenza, TipoEvento } from '@calcio/shared';
import { formattaMinuto, puo } from '@calcio/shared';
import {
  InfoIcon,
  ListOrderedIcon,
  RotateCcwIcon,
  Trash2Icon,
  TriangleAlertIcon,
} from 'lucide-react';
import { toast } from 'sonner';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { cn } from '@/lib/utils';
import { chiama } from '../api/client.js';
import { StatoCaricamento } from '../comuni/Testata.js';
import { BarraScheda, CardVista } from '../comuni/Riquadri.js';
import { ComandoIcona, PulsanteNuovo } from '../comuni/Azioni.js';
import { formatoIstante } from '../comuni/date.js';
import { messaggio, useDati } from '../comuni/dati.js';
import { useContesto } from '../navigazione/contesto.js';
import { NonTrovata } from '../navigazione/NonTrovata.js';
import { EventoManuale } from '../timer/EventoManuale.js';
import type { GiocatoreInRosa } from '../timer/tipi.js';
import { IconaEvento } from './eventi.js';
import { ETICHETTA_EVENTO, type NomeGiocatore, nomeBreve } from './partita.js';
import type { IntestazionePartita } from './Tabellino.js';
import { usePartita } from './contesto.js';
import { StatoVuoto } from '../comuni/StatoVuoto.js';
import { Nota } from '../comuni/Nota.js';

interface EventoInElenco {
  id: string;
  tipo: TipoEvento;
  giocatoreId: string | null;
  periodo: number;
  minuto: number;
  secondo: number;
  orarioReale: string;
  autore: string | null;
  cancellatoIl: string | null;
  cancellatoDa: string | null;
}

interface RispostaEventi {
  partita: IntestazionePartita;
  formato: Formato;
  tipiEventoAttivi: TipoEvento[];
  eventi: EventoInElenco[];
  incoerenze: Incoerenza[];
  rosa: GiocatoreInRosa[];
  giocatori: NomeGiocatore[];
}

const DI_PERIODO: TipoEvento[] = ['inizio_periodo', 'fine_periodo'];

const formatoCancellazione = formatoIstante({
  day: 'numeric',
  month: 'short',
  hour: '2-digit',
  minute: '2-digit',
});

export function ElencoEventi() {
  const { ss, id } = useParams<{ ss: string; id: string }>();
  const { utente } = useContesto();
  const partita = usePartita();
  const opera = Boolean(ss && puo(utente.assegnazioni, 'timer.opera', ss));
  const { dati, errore, ricarica } = useDati<RispostaEventi>(opera ? `/partite/${id}/eventi` : '');
  const [mostraCancellati, setMostraCancellati] = useState(false);
  const [aggiunta, setAggiunta] = useState(false);

  if (!ss || !id || !opera) return <NonTrovata />;
  if (!dati) return <StatoCaricamento errore={errore} />;

  // Durante la partita la correzione passa dal timer, che tiene la sessione.
  if (dati.partita.stato !== 'conclusa') {
    return (
      <Alert>
        <InfoIcon />
        <AlertTitle>Partita non ancora conclusa</AlertTitle>
        <AlertDescription>
          Gli eventi si correggono da qui a partita conclusa. Prima si registrano dal timer.
        </AlertDescription>
      </Alert>
    );
  }

  const per = new Map(dati.giocatori.map((g) => [g.id, g]));
  const cancellati = dati.eventi.filter((e) => e.cancellatoIl).length;
  const visibili = dati.eventi.filter((e) => mostraCancellati || !e.cancellatoIl);
  const minuto = (e: { minuto: number; periodo: number }) =>
    formattaMinuto(e.minuto, e.periodo, dati.formato);

  // Eventi, risultato e marcatori della testata cambiano insieme.
  const aggiorna = async () => {
    await Promise.all([ricarica(), partita?.ricarica()]);
  };

  const agisci = async (lavoro: Promise<unknown>, esito: string) => {
    try {
      await lavoro;
      await aggiorna();
      toast.success(esito);
    } catch (e) {
      toast.error(messaggio(e));
    }
  };

  return (
    <>
      <BarraScheda
        comandi={
          <>
            <div className="flex items-center gap-2">
              <Switch
                id="eventi-cancellati"
                checked={mostraCancellati}
                onCheckedChange={setMostraCancellati}
                disabled={cancellati === 0}
              />
              <Label htmlFor="eventi-cancellati">Mostra i cancellati</Label>
            </div>
            <PulsanteNuovo etichetta="Aggiungi evento" onClick={() => setAggiunta(true)} />
          </>
        }
      >
        <span className="flex items-center gap-1 text-muted-foreground" title="Eventi">
          <ListOrderedIcon aria-hidden className="size-4" />
          <span className="font-semibold text-foreground tabular-nums">
            {dati.eventi.length - cancellati}
          </span>
          eventi
        </span>
        <span className="flex items-center gap-1 text-muted-foreground" title="Cancellati">
          <Trash2Icon aria-hidden className="size-4" />
          <span className="font-semibold text-foreground tabular-nums">{cancellati}</span>
          cancellati
        </span>
      </BarraScheda>

      {dati.incoerenze.length > 0 && (
        <Alert>
          <TriangleAlertIcon />
          <AlertTitle>Il ricalcolo ha trovato delle incoerenze</AlertTitle>
          <AlertDescription>
            <ul className="list-disc pl-4">
              {dati.incoerenze.map((i) => {
                const evento = dati.eventi.find((e) => e.id === i.eventoId);
                return (
                  <li key={`${i.eventoId}-${i.messaggio}`}>
                    {evento && `${minuto(evento)} ${ETICHETTA_EVENTO[evento.tipo]}: `}
                    {i.messaggio}
                  </li>
                );
              })}
            </ul>
          </AlertDescription>
        </Alert>
      )}

      <CardVista titolo="Eventi" Icona={ListOrderedIcon} conteggio={visibili.length}>
        <Nota className="px-1">
          Ogni modifica ricalcola da capo minuti giocati e partecipazioni: chi non risulta più in
          campo perde il voto della partita.
        </Nota>
        {visibili.length === 0 ? (
          <StatoVuoto titolo="Nessun evento registrato" />
        ) : (
          <div className="overflow-x-auto rounded-lg border border-border bg-card">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-16">Minuto</TableHead>
                  <TableHead>Evento</TableHead>
                  <TableHead>Giocatore</TableHead>
                  <TableHead>Registro</TableHead>
                  <TableHead className="w-12">
                    <span className="sr-only">Azioni</span>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {visibili.map((e, i) => {
                  const incoerente = dati.incoerenze.some((x) => x.eventoId === e.id);
                  const spento = e.cancellatoIl && 'text-muted-foreground line-through';
                  return (
                    <Fragment key={e.id}>
                      {e.periodo !== visibili[i - 1]?.periodo && (
                        <TableRow className="bg-muted/50 hover:bg-muted/50">
                          <TableCell
                            colSpan={5}
                            className="py-1.5 text-[12px] font-semibold tracking-wide text-muted-foreground uppercase"
                          >
                            {e.periodo}° tempo
                          </TableCell>
                        </TableRow>
                      )}
                      <TableRow
                        className={cn(incoerente && 'bg-destructive/10 hover:bg-destructive/15')}
                      >
                        <TableCell className={cn('font-semibold tabular-nums', spento)}>
                          {minuto(e)}
                        </TableCell>
                        <TableCell>
                          <span className={cn('flex items-center gap-2', spento)}>
                            <IconaEvento
                              tipo={e.tipo}
                              className={cn(e.cancellatoIl && 'opacity-50')}
                            />
                            {ETICHETTA_EVENTO[e.tipo]}
                          </span>
                        </TableCell>
                        <TableCell className={cn(spento)}>
                          {e.giocatoreId ? (
                            nomeBreve(per.get(e.giocatoreId))
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </TableCell>
                        <TableCell className="text-[13px] text-muted-foreground">
                          {e.cancellatoIl
                            ? `Cancellato da ${e.cancellatoDa ?? '—'}, ${formatoCancellazione.format(new Date(e.cancellatoIl))}`
                            : (e.autore ?? '—')}
                        </TableCell>
                        <TableCell className="py-1 text-right">
                          {DI_PERIODO.includes(e.tipo) ? null : e.cancellatoIl ? (
                            <ComandoIcona
                              etichetta="Ripristina l'evento"
                              Icona={RotateCcwIcon}
                              size="icon-sm"
                              onClick={() =>
                                void agisci(
                                  chiama(`/partite/${id}/eventi/${e.id}/ripristina`, {
                                    metodo: 'POST',
                                  }),
                                  'Evento ripristinato.',
                                )
                              }
                            />
                          ) : (
                            <ComandoIcona
                              etichetta="Cancella l'evento"
                              Icona={Trash2Icon}
                              size="icon-sm"
                              onClick={() =>
                                void agisci(
                                  chiama(`/partite/${id}/eventi/${e.id}`, { metodo: 'DELETE' }),
                                  'Evento cancellato: resta tra i cancellati, da cui si ripristina.',
                                )
                              }
                            />
                          )}
                        </TableCell>
                      </TableRow>
                    </Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </CardVista>

      {/* Il minuto si chiede ogni volta e si valida con le stesse regole del
        server: a partita conclusa il cronometro non lo fornisce piu. */}
      <EventoManuale
        partitaId={id}
        rosa={dati.rosa}
        tipiAttivi={dati.tipiEventoAttivi}
        formato={dati.formato}
        eventi={dati.eventi}
        aperto={aggiunta}
        onChiudi={() => setAggiunta(false)}
        onAggiunto={() => {
          toast.success('Evento aggiunto.');
          void aggiorna();
        }}
      />
    </>
  );
}
